import React from 'react';
import { format } from 'date-fns';

const SessionCalendar = ({ 
  sessions, 
  sessionCardComponent: SessionCardComponent,
  sessionCardProps = {},
  showDateHeaders = true,
  showSessionCount = true,
  layout = 'grid',
  className = ""
}) => {
  // Helper function to group sessions by date
  const groupSessionsByDate = (sessions) => {
    const grouped = {};
    
    sessions.forEach(session => {
      // Handle different date formats that might come from the database
      let sessionDate;
      if (session.session_date) {
        sessionDate = new Date(session.session_date);
      } else if (session.date) {
        sessionDate = new Date(session.date);
      } else {
        return; // Skip sessions without dates
      }
      
      // Check if the date is valid
      if (!sessionDate || isNaN(sessionDate.getTime())) {
        console.warn('Invalid session date:', session.session_date || session.date);
        return;
      }
      
      // Normalize to date only (remove time)
      const dateKey = sessionDate.toISOString().split('T')[0];
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: sessionDate,
          sessions: []
        };
      }
      
      grouped[dateKey].sessions.push(session);
    });
    
    // Sort sessions within each date group by time
    Object.values(grouped).forEach(group => {
      group.sessions.sort((a, b) => {
        const timeA = a.start_time || a.time || '00:00';
        const timeB = b.start_time || b.time || '00:00';
        return timeA.localeCompare(timeB);
      });
    });
    
    // Sort by date and return as array
    return Object.values(grouped).sort((a, b) => a.date - b.date);
  };

  // Helper function to format date header (similar to UpcomingSessions)
  const formatDateHeader = (date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Normalize dates to compare only the date part (ignore time)
    const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const tomorrowNormalized = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
    const sessionNormalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const isToday = sessionNormalized.getTime() === todayNormalized.getTime();
    const isTomorrow = sessionNormalized.getTime() === tomorrowNormalized.getTime();
    
    if (isToday) {
      return `Today, ${date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`;
    } else if (isTomorrow) {
      return `Tomorrow, ${date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  const groupedSessions = groupSessionsByDate(sessions);

  if (sessions.length === 0) {
    return null;
  }

  const isTimeline = layout === 'timeline';

  return (
    <div className={`${isTimeline ? 'space-y-6' : 'space-y-8'} ${className}`}>
      {groupedSessions.map((dayGroup, dayIndex) => (
        <section key={dayIndex} className={isTimeline ? 'space-y-2' : 'space-y-4'}>
          {showDateHeaders && (
            <div className="flex items-center gap-3 pb-1">
              <div className="flex-1 min-w-0">
                <h2 className={`font-bold text-text-primary ${isTimeline ? 'text-base sm:text-lg' : 'text-2xl'}`}>
                  {formatDateHeader(dayGroup.date)}
                </h2>
                {!isTimeline && (
                  <div className="mt-1 h-1 w-32 rounded-full bg-gradient-to-r from-primary/50 to-primary" />
                )}
              </div>
              {showSessionCount && (
                <span className="text-xs sm:text-sm text-text-muted shrink-0">
                  {dayGroup.sessions.length} session{dayGroup.sessions.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}

          {isTimeline ? (
            <ul className="space-y-2">
              {dayGroup.sessions.map((session) => (
                <SessionCardComponent
                  key={session.id}
                  session={session}
                  {...sessionCardProps}
                  userSignupStatus={sessionCardProps.userSignupStatus?.[session.id]}
                />
              ))}
            </ul>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-visible">
              {dayGroup.sessions.map((session) => (
                <SessionCardComponent
                  key={session.id}
                  session={session}
                  {...sessionCardProps}
                  userSignupStatus={sessionCardProps.userSignupStatus?.[session.id]}
                />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
};

export default SessionCalendar;
